/**
 * StateMachine Integration with FinGame
 * Manages state transitions and UI control manipulation for the fill-in puzzle game
 */

import { StateMachine } from "./StateMachine.js";
import stateConfig from "./stateConfig.json";

export function setupStateMachine(finGame) {
  // Create state machine
  const stateMachine = new StateMachine(stateConfig);

  // === Helper Functions for UI Control ===

  const enableMenuButtons = () => {
    document.querySelector("#rebus-btn")?.removeAttribute("disabled");
    document.querySelector("#clear-btn")?.removeAttribute("disabled");
    document.querySelector("#reveal-btn")?.removeAttribute("disabled");
    document.querySelector("#check-btn")?.removeAttribute("disabled");
    // Help button is NEVER disabled
  };

  const disableMenuButtons = () => {
    document.querySelector("#rebus-btn")?.setAttribute("disabled", "true");
    document.querySelector("#clear-btn")?.setAttribute("disabled", "true");
    document.querySelector("#reveal-btn")?.setAttribute("disabled", "true");
    document.querySelector("#check-btn")?.setAttribute("disabled", "true");
    // Help button is NEVER disabled
  };

  const updatePlayButton = (text) => {
    const playBtn = document.querySelector("#play-btn");
    if (playBtn) playBtn.textContent = text;
  };

  const updateStatusDisplay = (status) => {
    const statusElem = document.querySelector("#game-status");
    if (statusElem) statusElem.textContent = `Status: ${status}`;
  };

  const showCompletionModal = () => {
    const overlay = document.querySelector("#completion-overlay");
    const modal = document.querySelector("#completion-modal");

    if (overlay && modal) {
      // Use requestAnimationFrame to ensure canvas render completes, then delay slightly
      // to allow the user to see the final character displayed before modal appears
      requestAnimationFrame(() => {
        setTimeout(() => {
          overlay.classList.add("show");
          modal.classList.add("show");
          document.body.classList.add("modal-open");
          console.log("[Completion Modal] Modal shown after render");
        }, 300);
      });
    }
  };

  const hideCompletionModal = () => {
    const overlay = document.querySelector("#completion-overlay");
    const modal = document.querySelector("#completion-modal");

    if (overlay && modal) {
      overlay.classList.remove("show");
      modal.classList.remove("show");
      document.body.classList.remove("modal-open");
      console.log("[Completion Modal] Modal hidden");
    }
  };

  const showHelpModal = (helpTitle, helpItems, stateSpecificItem = null) => {
    const overlay = document.querySelector("#help-overlay");
    const modal = document.querySelector("#help-modal");
    const bodyContent = document.querySelector("#help-modal-body");

    if (overlay && modal && bodyContent) {
      // Build HTML content for help modal
      let htmlContent = `<h2>${helpTitle}</h2>`;

      // Add state-specific item if provided
      if (stateSpecificItem) {
        htmlContent += `<p>${stateSpecificItem}</p>`;
      }

      // Add main help items
      htmlContent += "<h3>Keyboard Controls</h3><ul>";
      helpItems.forEach((item) => {
        htmlContent += `<li>${item}</li>`;
      });
      htmlContent += "</ul>";

      // Add menu controls section
      htmlContent += `
        <h3>Menu Controls</h3>
        <ul>
          <li><strong>Clear:</strong> Remove tile, word, or entire puzzle</li>
          <li><strong>Reveal:</strong> Show solution for tile, word, or puzzle</li>
          <li><strong>Check:</strong> Verify your answers</li>
        </ul>
        <div class="note">
          💡 Note: Using reveal features will mark puzzle as "Filled" instead of "Complete"
        </div>
      `;

      bodyContent.innerHTML = htmlContent;

      // Show modal with same animation as completion modal
      overlay.classList.add("show");
      modal.classList.add("show");
      document.body.classList.add("modal-open");
      console.log("[Help Modal] Modal shown");
    }
  };

  const hideHelpModal = () => {
    const overlay = document.querySelector("#help-overlay");
    const modal = document.querySelector("#help-modal");

    if (overlay && modal) {
      overlay.classList.remove("show");
      modal.classList.remove("show");
      document.body.classList.remove("modal-open");
      console.log("[Help Modal] Modal hidden");
    }
  };

  // === State Entry Handlers ===

  stateMachine.onEnter("LOADING", ({ data }) => {
    console.log("Loading puzzle...", data);
    updateStatusDisplay("Loading...");
    disableMenuButtons();
    updatePlayButton("Loading...");

    // Disable canvas interaction
    const canvas = document.querySelector("#fillincanvas");
    if (canvas) canvas.style.pointerEvents = "none";
  });

  stateMachine.onEnter("READY", () => {
    console.log("Puzzle ready to start");
    updateStatusDisplay("Ready");
    disableMenuButtons();
    updatePlayButton("Play");

    // Initialize progress to 0%
    if (finGame.updateProgress) finGame.updateProgress();

    // Enable play button
    document.querySelector("#play-btn")?.removeAttribute("disabled");
  });

  stateMachine.onEnter("PLAYING", () => {
    console.log("Game playing");
    updateStatusDisplay("Playing");

    // Start timer if available
    if (finGame.timer) finGame.timer.start();

    // Initialize progress display
    if (finGame.updateProgress) finGame.updateProgress();

    // Enable all menu controls
    enableMenuButtons();
    updatePlayButton("Pause");

    // Enable canvas interaction
    const canvas = document.querySelector("#fillincanvas");
    if (canvas) {
      canvas.style.pointerEvents = "auto";
      canvas.focus();
    }

    // Activate game controller
    if (finGame.gameController) finGame.gameController.activate();
  });

  stateMachine.onEnter("PAUSED", () => {
    console.log("Game paused");
    updateStatusDisplay("Paused");

    // Stop timer
    if (finGame.timer) finGame.timer.stop();

    // Disable menu buttons (help always remains enabled)
    disableMenuButtons();

    // Update play button to resume
    updatePlayButton("Resume");

    // Deactivate game controller
    if (finGame.gameController) finGame.gameController.deactivate();

    // Disable canvas interaction
    const canvas = document.querySelector("#fillincanvas");
    if (canvas) canvas.style.pointerEvents = "none";
  });

  stateMachine.onEnter("CHECKING", ({ data }) => {
    console.log("Checking answers...", data);
    updateStatusDisplay("Checking...");

    // Temporarily disable controls
    disableMenuButtons();

    // Check the word/grid based on data.checkAll
    let isCorrect = false;
    if (data.checkType == "puzzle") {
      // Check entire puzzle (placeholder - implement in FinGame)
      isCorrect = finGame.checkPuzzle ? finGame.checkPuzzle() : false;
    } else if (data.checkType == "word") {
      // Check current word (placeholder - implement in FinGame)
      isCorrect = finGame.checkWord ? finGame.checkWord() : false;
    } else if (data.checkType == "tile") {
      console.log("Hello Check.");
      // Check current tile (placeholder - implemen in FinGame)
      isCorrect = finGame.checkTile ? finGame.checkTile() : false;
    }

    // Show visual feedback (could be a flash or message)
    const statusElem = document.querySelector("#game-status");
    if (statusElem) {
      statusElem.textContent = isCorrect
        ? "Status: Correct!"
        : "Status: Incorrect";
      statusElem.style.color = isCorrect ? "green" : "red";
      setTimeout(() => {
        statusElem.style.color = "";
      }, 2000);
    }

    // Return to playing
    setTimeout(() => {
      stateMachine.transition("PLAYING");
    }, 1000);
  });

  stateMachine.onEnter("REVEALING", ({ data }) => {
    console.log("Revealing hint...", data);
    updateStatusDisplay("Revealing...");

    // Mark that reveals were used (except for puzzle reveal which handles it internally)
    if (data.revealType !== "puzzle") {
      finGame.revealsUsed = true;
    }

    // Call appropriate reveal method on finGame
    if (data.revealType === "tile") {
      // Reveal current tile
      if (finGame.solveTile) finGame.solveTile();
    } else if (data.revealType === "word") {
      // Reveal current word
      if (finGame.solveWord) finGame.solveWord();
    } else if (data.revealType === "puzzle") {
      // Reveal entire puzzle (solvePuzzle sets revealsUsed internally)
      if (finGame.solvePuzzle) finGame.solvePuzzle();
    }

    // Update progress after reveal
    if (finGame.updateProgress) finGame.updateProgress();

    // Return to playing after tile/word reveal, or transition to COMPLETE for puzzle reveal
    if (data.revealType === "puzzle") {
      setTimeout(() => {
        stateMachine.transition("COMPLETE", { revealed: true });
      }, 500);
    } else {
      setTimeout(() => {
        stateMachine.transition("PLAYING");
      }, 500);
    }
  });

  stateMachine.onEnter("COMPLETE", ({ data }) => {
    console.log("Puzzle complete!");

    // Check if puzzle was legitimately solved or revealed
    const wasRevealed = finGame.revealsUsed || (data && data.revealed);

    if (wasRevealed) {
      updateStatusDisplay("Filled");
      console.log("Puzzle filled via reveals - no completion honor");
    } else {
      updateStatusDisplay("Complete!");
    }

    // Stop timer
    if (finGame.timer) finGame.timer.stop();

    // Update progress to 100%
    const progressElem = document.querySelector("#game-progress");
    if (progressElem) progressElem.textContent = "Progress: 100%";

    // Disable menu buttons
    disableMenuButtons();

    // Update play button to "Reset"
    updatePlayButton("Reset");

    // Show completion message only if legitimately solved (no reveals used)
    if (!wasRevealed) {
      showCompletionModal();
    }

    // Deactivate game controller
    if (finGame.gameController) finGame.gameController.deactivate();
  });

  stateMachine.onEnter("ERROR", ({ data }) => {
    console.error("Error occurred:", data.error);
    updateStatusDisplay("Error");

    // Show error message
    const errorMsg = data.error?.message || data.error || "Unknown error";
    alert(`Error: ${errorMsg}`);

    // Stop timer if running
    if (finGame.timer) finGame.timer.stop();

    // Disable all controls
    disableMenuButtons();
    updatePlayButton("Retry");
  });

  // === State Exit Handlers ===

  stateMachine.onExit("PAUSED", () => {
    // Re-enable controls when exiting pause
    enableMenuButtons();
  });

  // === Global Transition Handler ===

  stateMachine.onTransition(({ from, to, data }) => {
    console.log(`State: ${from} → ${to}`, data);

    // Update UI state indicator
    document.querySelector("#state-indicator")?.setAttribute("data-state", to);

    // Log to analytics
    if (window.analytics) {
      window.analytics.track("state_change", { from, to, data });
    }
  });

  // === Wire up UI controls ===

  // Play/Pause button (main control)
  document.querySelector("#play-btn")?.addEventListener("click", () => {
    const currentState = stateMachine.getState();

    if (currentState === "READY") {
      // Start game
      stateMachine.transition("PLAYING");
      if (finGame.playGame) finGame.playGame();
    } else if (currentState === "PLAYING") {
      // Pause game
      stateMachine.transition("PAUSED");
    } else if (currentState === "PAUSED") {
      // Resume game
      stateMachine.transition("PLAYING");
    } else if (currentState === "COMPLETE") {
      // Start new game
      stateMachine.transition("LOADING", { restart: true });
      if (finGame.resetGame) {
        finGame.resetGame();
        stateMachine.transition("READY");
        // finGame.playGame();
      }
    }
  });

  // Handle menu actions from dropdown items
  document.addEventListener("menuAction", function (e) {
    const { action } = e.detail;
    const currentState = stateMachine.getState();

    // Only process actions when playing
    if (currentState !== "PLAYING") {
      console.warn(`Action ${action} not available in ${currentState} state`);
      return;
    }

    switch (action) {
      case "clear-tile":
        if (finGame.clearTile) finGame.clearTile();
        break;
      case "clear-word":
        if (finGame.clearWord) finGame.clearWord();
        break;
      case "clear-puzzle":
        if (finGame.resetGame) {
          finGame.resetGame();
          stateMachine.transition("READY");
        }
        break;

      case "reveal-tile":
        stateMachine.transition("REVEALING", { revealType: "tile" });
        break;
      case "reveal-word":
        stateMachine.transition("REVEALING", { revealType: "word" });
        break;
      case "reveal-puzzle":
        stateMachine.transition("REVEALING", { revealType: "puzzle" });
        break;

      case "check-tile":
        stateMachine.transition("CHECKING", {
          checkAll: false,
          checkType: "tile",
        });
        break;
      case "check-word":
        stateMachine.transition("CHECKING", {
          checkAll: false,
          checkType: "word",
        });
        break;
      case "check-puzzle":
        stateMachine.transition("CHECKING", {
          checkAll: true,
          checkType: "puzzle",
        });
        break;
    }
  });

  // Help button - accessible from ANY state
  document.querySelector("#help-btn")?.addEventListener("click", () => {
    const currentState = stateMachine.getState();

    console.log("Showing help...");

    // Define base help items
    const baseHelpItems = [
      "Use arrow keys or mouse to navigate cells",
      "Type letters to fill cells",
      "Backspace/Delete to erase",
      "Space bar to toggle across/down orientation",
      "Tab to jump to next word",
      "Shift+Tab to jump to previous word",
    ];

    // Customize help message based on current state
    let stateSpecificItem = null;
    if (currentState === "READY") {
      stateSpecificItem =
        '👉 Click "Play" to start the puzzle and enable menu controls';
    } else if (currentState === "PAUSED") {
      stateSpecificItem =
        '👉 Click "Resume" to continue playing or "Reset" to start a new puzzle';
    }

    showHelpModal("Fill-in Puzzle Help", baseHelpItems, stateSpecificItem);
  });

  // Print button - accessible from ANY state
  document.querySelector("#print-btn")?.addEventListener("click", () => {
    console.log("[Print] Print puzzle button clicked");
    if (finGame?.printPuzzle) {
      finGame.printPuzzle();
    } else {
      console.warn("[Print] finGame.printPuzzle not available");
      alert("Print puzzle feature is not available");
    }
  });

  // === Hamburger Menu Setup ===

  const hamburgerBtn = document.querySelector("#hamburger-menu");
  const hamburgerNav = document.querySelector("#hamburger-nav");
  const hamburgerOverlay = document.querySelector("#hamburger-overlay");
  const hamburgerClose = document.querySelector("#hamburger-close");

  if (hamburgerBtn && hamburgerNav && hamburgerOverlay) {
    console.log("[StateMachine] Setting up hamburger menu");

    // Open menu
    hamburgerBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      hamburgerNav.classList.add("show");
      hamburgerOverlay.classList.add("show");
      document.body.classList.add("menu-open");
      console.log("[Hamburger] Menu opened");
    });

    // Close menu
    const closeMenu = () => {
      hamburgerNav.classList.remove("show");
      hamburgerOverlay.classList.remove("show");
      document.body.classList.remove("menu-open");
      console.log("[Hamburger] Menu closed");
    };

    hamburgerClose?.addEventListener("click", closeMenu);
    hamburgerOverlay.addEventListener("click", closeMenu);

    // Handle navigation links
    hamburgerNav.querySelectorAll("[data-nav]").forEach((link) => {
      link.addEventListener("click", (e) => {
        const navTarget = e.target.getAttribute("data-nav");
        console.log("[Hamburger] Navigation to:", navTarget);
        // Allow default navigation behavior
        closeMenu();
      });
    });

    // Handle print actions
    hamburgerNav.querySelectorAll("[data-action]").forEach((button) => {
      button.addEventListener("click", (e) => {
        const action = e.target.getAttribute("data-action");
        console.log("[Hamburger] Action:", action);

        switch (action) {
          case "print-puzzle":
            if (finGame?.printPuzzle) {
              finGame.printPuzzle();
            } else {
              console.warn("[Hamburger] finGame.printPuzzle not available");
              alert("Print puzzle feature is not available");
            }
            break;
          case "print-solution":
            if (finGame?.printSolution) {
              finGame.printSolution();
            } else {
              console.warn("[Hamburger] finGame.printSolution not available");
              alert("Print solution feature is not available");
            }
            break;
        }

        closeMenu();
      });
    });

    // Close menu on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && hamburgerNav.classList.contains("show")) {
        closeMenu();
      }
    });
  }

  // === Completion Modal Setup ===
  const completionCloseBtn = document.querySelector("#completion-modal-close");
  const completionOverlay = document.querySelector("#completion-overlay");

  if (completionCloseBtn && completionOverlay) {
    console.log("[StateMachine] Setting up completion modal");

    // Close modal on close button
    completionCloseBtn.addEventListener("click", () => {
      hideCompletionModal();
    });

    // Close modal on overlay click
    completionOverlay.addEventListener("click", () => {
      hideCompletionModal();
    });

    // Close modal on Escape key
    document.addEventListener("keydown", (e) => {
      if (
        e.key === "Escape" &&
        document.querySelector("#completion-modal").classList.contains("show")
      ) {
        hideCompletionModal();
      }
    });
  }

  // === Help Modal Setup ===
  const helpCloseBtn = document.querySelector("#help-modal-close");
  const helpOverlay = document.querySelector("#help-overlay");

  if (helpCloseBtn && helpOverlay) {
    console.log("[StateMachine] Setting up help modal");

    // Close modal on close button
    helpCloseBtn.addEventListener("click", () => {
      hideHelpModal();
    });

    // Close modal on overlay click
    helpOverlay.addEventListener("click", () => {
      hideHelpModal();
    });

    // Close modal on Escape key
    document.addEventListener("keydown", (e) => {
      if (
        e.key === "Escape" &&
        document.querySelector("#help-modal").classList.contains("show")
      ) {
        hideHelpModal();
      }
    });
  }

  // === Settings button (gear icon) ===

  const settingsBtn = document.querySelector("#settings-btn");
  const settingsMenu = document.querySelector("#settings-menu");
  console.log("[StateMachine] Settings button:", settingsBtn);
  console.log("[StateMachine] Settings menu:", settingsMenu);
  if (settingsBtn && settingsMenu) {
    console.log("[StateMachine] Setting up settings menu event listeners");
    // Toggle menu visibility on button click
    settingsBtn.addEventListener("click", (e) => {
      console.log("[StateMachine] Settings button clicked!");
      e.stopPropagation();
      settingsMenu.classList.toggle("show");
      console.log(
        "[StateMachine] Menu should now have show class:",
        settingsMenu.classList.contains("show"),
      );
    });

    // Hide menu when clicking outside
    document.addEventListener("click", (e) => {
      if (!settingsMenu.contains(e.target) && e.target !== settingsBtn) {
        settingsMenu.classList.remove("show");
      }
    });

    // Mode switching with smooth transitions
    const setMode = (mode) => {
      document.body.classList.remove("light-mode", "dark-mode");
      if (mode) {
        document.body.classList.add(mode);
        // Store preference
        localStorage.setItem("colorMode", mode);
      }
    };

    // Font size management
    const DEFAULT_FONT_SIZE = 14; // Default base font size from CSS
    let currentFontSize =
      parseInt(localStorage.getItem("fontSize")) || DEFAULT_FONT_SIZE;
    const MIN_FONT_SIZE = 10;
    const MAX_FONT_SIZE = 20;

    const updateFontSize = (size) => {
      document.documentElement.style.setProperty(
        "--base-font-size",
        `${size}px`,
      );
      localStorage.setItem("fontSize", size);
    };

    // Restore saved preferences on load
    const savedMode = localStorage.getItem("colorMode");
    if (savedMode) setMode(savedMode);
    updateFontSize(currentFontSize);

    // Handle menu item clicks
    settingsMenu.addEventListener("click", (e) => {
      const item = e.target.closest("[data-action]");
      if (!item) return;

      const action = item.getAttribute("data-action");
      settingsMenu.classList.remove("show");

      switch (action) {
        case "dark-mode":
          setMode("dark-mode");
          console.log("[Settings] Dark mode enabled");
          break;
        case "light-mode":
          setMode("light-mode");
          console.log("[Settings] Light mode enabled");
          break;
        case "font-larger":
          currentFontSize = Math.min(currentFontSize + 1, MAX_FONT_SIZE);
          updateFontSize(currentFontSize);
          console.log("[Settings] Font size increased to", currentFontSize);
          break;
        case "font-reset":
          currentFontSize = DEFAULT_FONT_SIZE;
          updateFontSize(currentFontSize);
          console.log("[Settings] Font size reset to default", currentFontSize);
          break;
        case "font-smaller":
          currentFontSize = Math.max(currentFontSize - 1, MIN_FONT_SIZE);
          updateFontSize(currentFontSize);
          console.log("[Settings] Font size decreased to", currentFontSize);
          break;
        default:
          console.log("[Settings] Unknown menu action:", action);
      }
      window.handleSettingsMenu(action);
    });
  }

  // === Keyboard shortcuts ===

  document.addEventListener("keydown", (event) => {
    const currentState = stateMachine.getState();

    // Pause with Escape (only when playing)
    if (event.key === "Escape" && currentState === "PLAYING") {
      stateMachine.transition("PAUSED");
      event.preventDefault();
    }

    // Resume with Escape when paused
    if (event.key === "Escape" && currentState === "PAUSED") {
      stateMachine.transition("PLAYING");
      event.preventDefault();
    }

    // Quick reveal letter with Ctrl/Cmd + R
    if (
      (event.ctrlKey || event.metaKey) &&
      event.key === "r" &&
      currentState === "PLAYING"
    ) {
      stateMachine.transition("REVEALING", { revealType: "tile" });
      event.preventDefault();
    }
  });

  // === Puzzle completion detection ===

  // Hook into finGame to detect completion
  // This could be called from FinGame when all words are correctly placed
  finGame.onPuzzleComplete = () => {
    if (stateMachine.is("PLAYING")) {
      stateMachine.transition("COMPLETE");
    }
  };

  // Expose for debugging
  window.stateMachine = stateMachine;

  console.log("StateMachine initialized with state:", stateMachine.getState());

  return stateMachine;
}

// Usage:
// import { setupStateMachine } from './StateMachineIntegration.js';
// const stateMachine = setupStateMachine(window.finGame);
